import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store/app';
import { api } from '../lib/api';
import { Icon } from '../components/Icon';

interface Results {
  people: { id: number; name: string; role: string }[];
  clients: { id: number; name: string; industry: string }[];
  projects: { id: number; name: string; client_name: string }[];
}

const EMPTY: Results = { people: [], clients: [], projects: [] };

export function HeaderSearch() {
  const { openDrawer } = useApp();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [res, setRes] = useState<Results>(EMPTY);
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (q.trim().length < 2) {
      setRes(EMPTY);
      return;
    }
    const t = setTimeout(() => {
      api.get<Results>(`/search?q=${encodeURIComponent(q.trim())}`).then((r) => { setRes(r); setOpen(true); }).catch(() => setRes(EMPTY));
    }, 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const go = (fn: () => void) => { fn(); setOpen(false); setQ(''); };
  const total = res.people.length + res.clients.length + res.projects.length;

  return (
    <div ref={box} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 11px', width: 240, maxWidth: '32vw' }}>
        <Icon name="search" size={18} color="var(--text-3)" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => total && setOpen(true)} placeholder="Search CCSilo…" style={{ border: 0, background: 'transparent', outline: 'none', fontFamily: 'inherit', fontSize: 13, color: 'var(--text)', width: '100%' }} />
        {q && <span onClick={() => { setQ(''); setRes(EMPTY); }} style={{ cursor: 'pointer', display: 'inline-flex' }}><Icon name="close" size={16} color="var(--text-3)" /></span>}
      </div>
      {open && q.trim().length >= 2 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, width: 320, maxWidth: '80vw', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', zIndex: 60, overflow: 'hidden', maxHeight: 420, overflowY: 'auto' }}>
          {total === 0 && <div style={{ padding: 16, fontSize: 13, color: 'var(--text-3)', textAlign: 'center' }}>No matches for “{q}”.</div>}
          <Group label="People" items={res.people.map((p) => ({ key: `p${p.id}`, title: p.name, sub: p.role, icon: 'person', onClick: () => nav(`/people/${p.id}`) }))} go={go} />
          <Group label="Clients" items={res.clients.map((c) => ({ key: `c${c.id}`, title: c.name, sub: c.industry, icon: 'apartment', onClick: () => nav(`/clients/${c.id}`) }))} go={go} />
          <Group label="Projects" items={res.projects.map((p) => ({ key: `pr${p.id}`, title: p.name, sub: p.client_name, icon: 'view_kanban', onClick: () => { nav('/projects'); openDrawer({ type: 'project', mode: 'edit', id: p.id }); } }))} go={go} />
        </div>
      )}
    </div>
  );
}

function Group({ label, items, go }: { label: string; items: { key: string; title: string; sub: string; icon: string; onClick: () => void }[]; go: (fn: () => void) => void }) {
  if (!items.length) return null;
  return (
    <div>
      <div style={{ padding: '8px 12px 4px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
      {items.map((it) => (
        <div key={it.key} onClick={() => go(it.onClick)} className="cc-hover-s2 cc-clickable" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px' }}>
          <Icon name={it.icon} size={18} color="var(--text-3)" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.title}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>{it.sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
